import { useEffect, useState, useCallback } from "react";
import { api, fmtEUR } from "@/lib/api";
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
    ResponsiveContainer, Legend, PieChart, Pie, Cell,
} from "recharts";
import { Eye, Users, ShoppingCart, Euro, TrendingUp, TrendingDown, Download, Package, Eye as EyeIcon, ExternalLink } from "lucide-react";

const PRESETS = [
    { d: 7, label: "7d" },
    { d: 30, label: "30d" },
    { d: 90, label: "90d" },
    { d: 365, label: "1 ano" },
];

const SOURCE_COLORS = {
    direct: "#0A0A0A",
    search: "#E6007E",
    social: "#3B82F6",
    referral: "#F59E0B",
};

const SOURCE_LABELS = {
    direct: "Directo",
    search: "Pesquisa",
    social: "Redes Sociais",
    referral: "Referências",
};


function KpiCard({ icon: Icon, label, value, prev, deltaPct, formatter, isCurrency, accent }) {
    const positive = (deltaPct ?? 0) >= 0;
    const fmt = formatter || ((v) => v);
    return (
        <div
            className={`border-2 border-ink p-5 flex flex-col gap-2 ${accent ? "bg-magenta text-white" : "bg-white"}`}
            data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}
        >
            <div className="flex items-center justify-between">
                <span className={`label-mono ${accent ? "text-white/80" : ""}`}>{label}</span>
                <Icon size={18} className={accent ? "text-white" : "text-ink/60"} />
            </div>
            <div className="font-display font-black text-3xl tracking-tighter">
                {isCurrency ? fmtEUR(value) : fmt(value)}
            </div>
            {deltaPct !== null && deltaPct !== undefined && (
                <div className={`flex items-center gap-1 text-xs font-mono ${positive ? "text-emerald-600" : "text-rose-500"} ${accent ? "!text-white/80" : ""}`}>
                    {positive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                    {Math.abs(deltaPct)}% vs período anterior
                </div>
            )}
        </div>
    );
}


export default function AdminAnalytics() {
    const [days, setDays] = useState(30);
    const [overview, setOverview] = useState(null);
    const [series, setSeries] = useState([]);
    const [topViewed, setTopViewed] = useState([]);
    const [topSold, setTopSold] = useState([]);
    const [sources, setSources] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [ov, ts, tp, ts2, src] = await Promise.all([
                api.get(`/admin/analytics/overview?days=${days}`),
                api.get(`/admin/analytics/timeseries?days=${days}`),
                api.get(`/admin/analytics/top-products?days=${days}&limit=10`),
                api.get(`/admin/analytics/top-sold?days=${days}&limit=10`),
                api.get(`/admin/analytics/sources?days=${days}`),
            ]);
            setOverview(ov.data);
            setSeries(ts.data.series);
            setTopViewed(tp.data.items);
            setTopSold(ts2.data.items);
            setSources(src.data.items);
        } finally {
            setLoading(false);
        }
    }, [days]);

    useEffect(() => {
        load();
    }, [load]);

    const downloadCsv = async () => {
        const url = `${api.defaults.baseURL}/admin/analytics/export.csv?days=${days}`;
        const token = localStorage.getItem("sos_admin_token") || localStorage.getItem("sos_token");
        const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        const blob = await resp.blob();
        const a = document.createElement("a");
        a.href = window.URL.createObjectURL(blob);
        a.download = `analytics_${days}d.csv`;
        a.click();
    };

    const k = overview?.kpis || {};

    return (
        <div data-testid="admin-analytics">
            <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
                <h1 className="font-display font-black text-4xl tracking-tighter">Analytics</h1>
                <div className="flex items-center gap-2">
                    {PRESETS.map((p) => (
                        <button
                            key={p.d}
                            onClick={() => setDays(p.d)}
                            className={`border-2 border-ink px-4 py-2 text-xs font-bold uppercase tracking-wider ${
                                days === p.d ? "bg-ink text-white" : "bg-white hover:bg-ink hover:text-white"
                            }`}
                            data-testid={`range-${p.d}d`}
                        >
                            {p.label}
                        </button>
                    ))}
                    <button
                        onClick={downloadCsv}
                        className="border-2 border-ink bg-magenta text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-ink flex items-center gap-2"
                        data-testid="export-csv"
                    >
                        <Download size={14} /> CSV
                    </button>
                </div>
            </div>

            {loading && !overview ? (
                <div className="font-mono text-sm">A carregar…</div>
            ) : (
                <>
                    {/* KPI Cards */}
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                        <KpiCard icon={Eye} label="Visitas" value={k.page_views?.value ?? 0} deltaPct={k.page_views?.delta_pct} />
                        <KpiCard icon={Users} label="Visitantes únicos" value={k.visitors?.value ?? 0} deltaPct={k.visitors?.delta_pct} />
                        <KpiCard icon={EyeIcon} label="Produtos vistos" value={k.product_views?.value ?? 0} deltaPct={k.product_views?.delta_pct} />
                        <KpiCard icon={ShoppingCart} label="Encomendas" value={k.orders?.value ?? 0} deltaPct={k.orders?.delta_pct} />
                        <KpiCard icon={ShoppingCart} label="Encomendas pagas" value={k.paid_orders?.value ?? 0} deltaPct={k.paid_orders?.delta_pct} />
                        <KpiCard icon={Euro} label="Receita" value={k.revenue?.value ?? 0} deltaPct={k.revenue?.delta_pct} isCurrency accent />
                        <KpiCard icon={Euro} label="Ticket médio" value={k.aov?.value ?? 0} deltaPct={k.aov?.delta_pct} isCurrency />
                        <KpiCard icon={TrendingUp} label="Conversão %" value={k.conversion_pct?.value ?? 0} deltaPct={k.conversion_pct?.delta_pct} formatter={(v) => `${v}%`} />
                    </div>

                    {/* Time series chart */}
                    <div className="border-2 border-ink bg-white p-6 mb-8" data-testid="chart-timeseries">
                        <h2 className="font-display font-black text-xl tracking-tighter mb-4">Visitas vs Encomendas</h2>
                        <ResponsiveContainer width="100%" height={320}>
                            <LineChart data={series} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                                <CartesianGrid stroke="#0A0A0A" strokeOpacity={0.08} />
                                <XAxis dataKey="date" stroke="#0A0A0A" fontSize={11} tickFormatter={(d) => d.slice(5)} />
                                <YAxis yAxisId="left" stroke="#0A0A0A" fontSize={11} />
                                <YAxis yAxisId="right" orientation="right" stroke="#E6007E" fontSize={11} />
                                <Tooltip contentStyle={{ border: "2px solid #0A0A0A", borderRadius: 0, fontFamily: "monospace", fontSize: 12 }} />
                                <Legend wrapperStyle={{ fontFamily: "monospace", fontSize: 12 }} />
                                <Line yAxisId="left" type="monotone" dataKey="views" name="Visitas" stroke="#0A0A0A" strokeWidth={2} dot={false} />
                                <Line yAxisId="left" type="monotone" dataKey="visitors" name="Visitantes" stroke="#3B82F6" strokeWidth={2} dot={false} />
                                <Line yAxisId="right" type="monotone" dataKey="paid_orders" name="Encomendas pagas" stroke="#E6007E" strokeWidth={2} dot={{ r: 3 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Revenue chart */}
                    <div className="border-2 border-ink bg-white p-6 mb-8" data-testid="chart-revenue">
                        <h2 className="font-display font-black text-xl tracking-tighter mb-4">Receita diária (€)</h2>
                        <ResponsiveContainer width="100%" height={240}>
                            <BarChart data={series}>
                                <CartesianGrid stroke="#0A0A0A" strokeOpacity={0.08} />
                                <XAxis dataKey="date" stroke="#0A0A0A" fontSize={11} tickFormatter={(d) => d.slice(5)} />
                                <YAxis stroke="#0A0A0A" fontSize={11} />
                                <Tooltip
                                    contentStyle={{ border: "2px solid #0A0A0A", borderRadius: 0, fontFamily: "monospace", fontSize: 12 }}
                                    formatter={(v) => [`${Number(v).toFixed(2)} €`, "Receita"]}
                                />
                                <Bar dataKey="revenue" fill="#E6007E" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Two column: top viewed + top sold */}
                    <div className="grid lg:grid-cols-2 gap-8 mb-8">
                        <div className="border-2 border-ink bg-white" data-testid="top-viewed-table">
                            <div className="border-b-2 border-ink p-4 flex items-center justify-between">
                                <h2 className="font-display font-black text-lg tracking-tighter">Top produtos vistos</h2>
                                <Eye size={16} className="text-ink/60" />
                            </div>
                            {topViewed.length === 0 ? (
                                <div className="p-6 font-mono text-xs text-ink/60">Sem dados ainda. As visitas começam a registar quando os clientes navegarem o site.</div>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead className="bg-paper border-b-2 border-ink/10">
                                        <tr className="text-left">
                                            <th className="p-3 font-mono uppercase text-xs">Produto</th>
                                            <th className="p-3 font-mono uppercase text-xs text-right">Visitas</th>
                                            <th className="p-3 font-mono uppercase text-xs text-right">Únicos</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {topViewed.map((it) => (
                                            <tr key={it.product_id} className="border-b border-ink/5 hover:bg-paper">
                                                <td className="p-3">
                                                    <div className="font-bold text-sm leading-tight">{it.name || "—"}</div>
                                                    <div className="text-[11px] font-mono text-ink/60">{it.sku}</div>
                                                </td>
                                                <td className="p-3 text-right font-mono font-bold">{it.views}</td>
                                                <td className="p-3 text-right font-mono">{it.unique_visitors}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        <div className="border-2 border-ink bg-white" data-testid="top-sold-table">
                            <div className="border-b-2 border-ink p-4 flex items-center justify-between">
                                <h2 className="font-display font-black text-lg tracking-tighter">Top produtos vendidos</h2>
                                <Package size={16} className="text-ink/60" />
                            </div>
                            {topSold.length === 0 ? (
                                <div className="p-6 font-mono text-xs text-ink/60">Sem encomendas pagas no período.</div>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead className="bg-paper border-b-2 border-ink/10">
                                        <tr className="text-left">
                                            <th className="p-3 font-mono uppercase text-xs">Produto</th>
                                            <th className="p-3 font-mono uppercase text-xs text-right">Qtd</th>
                                            <th className="p-3 font-mono uppercase text-xs text-right">Receita</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {topSold.map((it, i) => (
                                            <tr key={`${it.product_id}-${i}`} className="border-b border-ink/5 hover:bg-paper">
                                                <td className="p-3">
                                                    <div className="font-bold text-sm leading-tight">{it.name || "—"}</div>
                                                    <div className="text-[11px] font-mono text-ink/60">{it.sku}</div>
                                                </td>
                                                <td className="p-3 text-right font-mono font-bold">{it.qty}</td>
                                                <td className="p-3 text-right font-mono">{fmtEUR(it.revenue)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>

                    {/* Sources */}
                    <div className="border-2 border-ink bg-white p-6" data-testid="sources-section">
                        <h2 className="font-display font-black text-xl tracking-tighter mb-4 flex items-center gap-2">
                            <ExternalLink size={18} /> Fontes de tráfego
                        </h2>
                        {sources.length === 0 ? (
                            <div className="font-mono text-xs text-ink/60">Sem visitas registadas no período.</div>
                        ) : (
                            <div className="grid md:grid-cols-2 gap-8">
                                <ResponsiveContainer width="100%" height={220}>
                                    <PieChart>
                                        <Pie
                                            data={sources.map((s) => ({ name: SOURCE_LABELS[s.source] || s.source, value: s.views, source: s.source }))}
                                            dataKey="value"
                                            cx="50%"
                                            cy="50%"
                                            outerRadius={80}
                                            label={(e) => `${e.name} (${e.value})`}
                                            fontFamily="monospace"
                                            fontSize={11}
                                        >
                                            {sources.map((s) => (
                                                <Cell key={s.source} fill={SOURCE_COLORS[s.source] || "#999"} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                                <table className="w-full text-sm self-center">
                                    <thead className="border-b-2 border-ink/10">
                                        <tr className="text-left">
                                            <th className="p-3 font-mono uppercase text-xs">Fonte</th>
                                            <th className="p-3 font-mono uppercase text-xs text-right">Visitas</th>
                                            <th className="p-3 font-mono uppercase text-xs text-right">Únicos</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sources.map((s) => (
                                            <tr key={s.source} className="border-b border-ink/5">
                                                <td className="p-3 font-bold flex items-center gap-2">
                                                    <span className="inline-block w-3 h-3 border border-ink" style={{ background: SOURCE_COLORS[s.source] || "#999" }} />
                                                    {SOURCE_LABELS[s.source] || s.source}
                                                </td>
                                                <td className="p-3 text-right font-mono font-bold">{s.views}</td>
                                                <td className="p-3 text-right font-mono">{s.visitors}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
